import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FaPercentage,
  FaSearch,
  FaLayerGroup,
  FaExchangeAlt,
  FaCalendarAlt,
  FaSyncAlt,
  FaArrowLeft,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const toISTDate = (ts) => ts
  ? new Date(new Date(ts).getTime() + IST_OFFSET_MS).toISOString().split("T")[0]
  : null;

export default function CommissionAudit() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // --- DATE FILTER STATE (Defaults to Today) ---
  const todayStr = new Date(Date.now() + IST_OFFSET_MS).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const filtered = (data.transactions || []).filter((tx) => {
        const isValidStatus = ["APPROVED", "DELIVERED"].includes(tx.status);
        const hasAmount = Number(tx.amount) > 0;
        const type = tx.paymentType?.toUpperCase().trim();
        const isValidType = ["IN", "OUT", "SPLITIN", "SPLITOUT"].includes(type);
        return isValidStatus && hasAmount && isValidType;
      });

      setTransactions(filtered);
    } catch (err) {
      console.error("[FETCH ERROR]", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- CORE AUDIT LOGIC ---
  const auditData = useMemo(() => {
    const normalPaid = [];
    const normalZero = [];
    const splitPaid = [];
    const splitZero = [];

    const groups = transactions.reduce((acc, tx) => {
      // Date Filter
      const txDate = toISTDate(tx.timestamp);
      if (startDate && txDate < startDate) return acc;
      if (endDate && txDate > endDate) return acc;
      if (
        tx.status === "PENDING" ||
        tx.status === "WAITING" ||
        tx.status === "REJECTED"
      )
        return acc; // Only consider completed transactions
      const parts = tx.paymentId.split("-");
      const baseId = parts[0] + "-" + parts[1];
      if (!acc[baseId]) acc[baseId] = [];
      acc[baseId].push(tx);
      return acc;
    }, {});

    Object.keys(groups).forEach((baseId) => {
      const cluster = groups[baseId];
      const isTPCluster = cluster.some((t) => t.paymentId.endsWith("-TP"));

      if (isTPCluster) {
        // TP cluster: origin = -TP entry, recipient = -LAK entry
        const tpEntry = cluster.find((t) => t.paymentId.endsWith("-TP"));
        const lakEntry = cluster.find((t) => t.paymentId.endsWith("-LAK"));
        if (!tpEntry) return;

        const txDate = toISTDate(tpEntry.timestamp);
        if (startDate && txDate < startDate) return;
        if (endDate && txDate > endDate) return;

        const originComm = Number(tpEntry.commission || 0);
        const totalRecipientComm = Number(lakEntry?.commission || 0);

        const matchesSearch =
          baseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tpEntry.fromBranch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tpEntry.thirdParty?.toLowerCase().includes(searchTerm.toLowerCase());
        if (!matchesSearch) return;

        const auditRow = {
          baseId,
          origin: tpEntry,
          recipients: lakEntry ? [lakEntry] : [],
          originComm,
          totalRecipientComm,
          date: txDate || "N/A",
          isTP: true,
        };

        if (originComm === 0 && totalRecipientComm === 0) {
          normalZero.push(auditRow);
        } else if (originComm > 0 && totalRecipientComm > 0) {
          normalPaid.push(auditRow);
        }
        return;
      }

      // Regular cluster
      const origin = cluster.find(
        (t) => t.flowRole === "SENDER" && !t.paymentId.includes("PART"),
      );
      const recipients = cluster.filter(
        (t) => t.flowRole === "RECEIVER" || t.paymentId.includes("PART"),
      );

      if (!origin) return;

      const isSplitAction =
        recipients.length > 1 ||
        cluster.some((t) => t.paymentType.includes("SPLIT"));
      const originComm = Number(origin.commission || 0);
      const totalRecipientComm = recipients.reduce(
        (sum, r) => sum + Number(r.commission || 0),
        0,
      );

      const auditRow = {
        baseId,
        origin,
        recipients,
        originComm,
        totalRecipientComm,
        date: toISTDate(origin.timestamp) || "N/A",
        isTP: false,
      };

      const matchesSearch =
        baseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        origin.fromBranch.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return;

      if (isSplitAction) {
        if (originComm === 0 && totalRecipientComm === 0) {
          splitZero.push(auditRow);
        } else if (originComm > 0 && totalRecipientComm > 0) {
          splitPaid.push(auditRow);
        }
      } else {
        if (originComm === 0 && totalRecipientComm === 0) {
          normalZero.push(auditRow);
        } else if (originComm > 0 && totalRecipientComm > 0) {
          normalPaid.push(auditRow);
        }
      }
    });

    return { normalPaid, normalZero, splitPaid, splitZero };
  }, [transactions, searchTerm, startDate, endDate]);

  const renderNormalTable = (data, title, color) => (
    <div style={{ marginBottom: "40px" }}>
      <h3 style={{ color, marginBottom: "10px" }}>
        {title} ({data.length})
      </h3>
      <div className="table-responsive">
        <table className="branch-table">
          <thead style={{ background: color + "15" }}>
            <tr>
              <th>ID</th>
              <th>DATE</th>
              <th>ORIGIN (FROM)</th>
              <th>TARGET (TO)</th>
              <th>AMOUNT</th>
              <th>ORIGIN COMM</th>
              <th>TARGET COMM</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.baseId} style={row.isTP ? { background: "#faf5ff" } : undefined}>
                <td>
                  {row.baseId}
                  {row.isTP && <span style={{ marginLeft: 5, fontSize: "0.68rem", background: "#7c3aed", color: "#fff", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>TP</span>}
                </td>
                <td>{row.date}</td>
                <td>{row.origin.fromBranch}</td>
                <td>
                  {row.isTP
                    ? <span style={{ color: "#7c3aed", fontWeight: 600 }}>{row.origin.thirdParty}</span>
                    : (row.recipients[0]?.toBranch || "N/A")}
                </td>
                <td>₹{Number(row.origin.amount).toLocaleString("en-IN")}</td>
                <td style={{ fontWeight: "bold", color }}>₹{row.originComm}</td>
                <td style={{ fontWeight: "bold", color }}>₹{row.totalRecipientComm}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSplitTable = (data, title, color) => (
    <div style={{ marginBottom: "40px" }}>
      <h3 style={{ color, marginBottom: "10px" }}>
        {title} ({data.length})
      </h3>
      <div className="table-responsive">
        <table className="branch-table">
          <thead style={{ background: color + "15" }}>
            <tr>
              <th style={{ width: "25%" }}>ORIGIN & DATE</th>
              <th style={{ width: "75%" }}>SPLIT PARTS</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.baseId}>
                <td
                  style={{
                    verticalAlign: "top",
                    borderRight: "2px solid #eee",
                  }}
                >
                  <div style={{ fontWeight: "bold" }}>{row.baseId}</div>
                  <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    Date: {row.date}
                  </div>
                  <div>
                    From: {row.origin.fromBranch}{" "}
                    <span>{row.origin.paymentType}</span>
                  </div>
                  <div>Amt: ₹{row.origin.amount.toLocaleString()}</div>
                  <div style={{ color, fontWeight: "bold" }}>
                    Comm: ₹{row.originComm}
                  </div>
                </td>
                <td style={{ padding: "0" }}>
                  <table style={{ width: "100%", margin: "0" }}>
                    <tbody>
                      {row.recipients.map((rec, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom:
                              idx === row.recipients.length - 1
                                ? "none"
                                : "1px solid #f1f5f9",
                          }}
                        >
                          <td style={{ width: "40%" }}>To: {rec.toBranch}</td>
                          <td style={{ width: "30%" }}>
                            Amt: ₹{rec.amount.toLocaleString()}
                          </td>
                          <td
                            style={{
                              width: "30%",
                              fontWeight: "bold",
                              color:
                                Number(rec.commission) > 0
                                  ? "#22c55e"
                                  : "#94a3b8",
                            }}
                          >
                            Comm: ₹{rec.commission || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="admin-container" style={{ padding: "20px" }}>
      {/* --- RESTORED HEADER --- */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          background: "#fff",
          padding: "15px 20px",
          borderRadius: "10px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FaPercentage color="#3b82f6" /> Commission Audit
        </h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <Link to="/commission-check">
            <button
              className="nav-btn"
              style={{ background: "#3b82f6", color: "#fff", border: "none", padding: "8px 15px", borderRadius: "5px", cursor: "pointer" }}

            >
              Commission Check
            </button>
          </Link>
          <button
            onClick={fetchData}
            className="br-btn"
            disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <FaSyncAlt className={loading ? "spin" : ""} />{" "}
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <Link to="/">
            <button
              className="Branch-nav"
            >
              <FaArrowLeft /> Back to Admin
            </button>
          </Link>
        </div>
      </header>

      {/* --- FILTER BAR --- */}
      <div
        style={{
          background: "#fff",
          padding: "15px",
          borderRadius: "10px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          marginBottom: "25px",
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FaSearch color="#94a3b8" />
          <input
            placeholder="Search Branch/ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "5px",
              border: "1px solid #ddd",
              outline: "none",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FaCalendarAlt color="#64748b" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span>to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button
          onClick={() => {
            setStartDate("");
            setEndDate("");
          }}
          className="br-btn-small"
          style={{
            background: "#f1f5f9",
            color: "#475569",
            border: "1px solid #cbd5e1",
          }}
        >
          View All History
        </button>
      </div>

      {/* --- CONTENT --- */}
      <div
        style={{
          background: "#f8fafc",
          padding: "20px",
          borderRadius: "12px",
          marginBottom: "30px",
        }}
      >
        <h2
          style={{
            borderBottom: "2px solid #cbd5e1",
            paddingBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FaExchangeAlt /> Normal Flow
        </h2>
        {renderNormalTable(auditData.normalPaid, "Paid Commissions", "#22c55e")}
        {renderNormalTable(auditData.normalZero, "Zero Commissions", "#ef4444")}
      </div>

      <div
        style={{ background: "#f1f5f9", padding: "20px", borderRadius: "12px" }}
      >
        <h2
          style={{
            borderBottom: "2px solid #cbd5e1",
            paddingBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FaLayerGroup /> Split Flow
        </h2>
        {renderSplitTable(auditData.splitPaid, "Split Paid", "#22c55e")}
        {renderSplitTable(auditData.splitZero, "Split Zero", "#ef4444")}
      </div>

    </div>
  );
}
